<template>
	<div style="display: none;">
		<button @click="createInstance">update</button>
	</div>
</template>

<script>
import { createPopper } from '@popperjs/core';

export default {
	name: 'PopperPosition',
	data() {
		return {
			trackedRef: () => {},
			popperRef: () => {},
			arrowRef: () => {},
			popperInstance: undefined,
			popperMinWidth: 'auto'
		};
	},
	props: {
		trackedRefName: {
			type: [Object, String, HTMLElement],
			default: undefined
		},
		popoverRefName: {
			type: [Object, String, HTMLElement],
			default: undefined
		},
		arrowRefName: {
			type: [Object, String, HTMLElement],
			default: undefined
		},
		placement: {
			type: String,
			default: 'auto'
		},
		popperVisible: {
			type: Boolean,
			default: false,
			required: true
		},
		modifiers: {
			type: Array,
			default: () => []
		},
		trackerWatch: {
			type: [Object, String, HTMLElement],
			default: null
		},
		strategy: {
			type: String,
			default: 'absolute'
		}
	},
	mounted() {
		if (this.popperVisible && this.trackedRefName && this.popoverRefName) {
			this.createInstance();
		}
	},
	watch: {
		popperVisible(newValue) {
			if (newValue) {
				if (this.popperInstance === undefined) {
					this.createInstance();
					return;
				}

				this.updateInstance();
			}
		},
		trackerWatch(newValue) {
			if (newValue) {
				this.createInstance();
			}
		},
		trackedRefName: {
			immediate: false,
			deep: false,
			handler(newValue) {
				if (newValue) {
					this.createInstance();
				}
			}
		},
		popoverRefName: {
			immediate: true,
			deep: false,
			handler(newValue) {
				if (newValue) {
					this.createInstance();
				}
			}
		},
		arrowRefName(newValue) {
			if (newValue) {
				this.createInstance();
			}
		},
		placement(newValue) {
			if (newValue) {
				this.createInstance();
			}
		}
	},
	methods: {
		onUpdate() {
			this.popperInstance.update();
		},
		createInstance() {
			const execInstance = this.execCreateInstance.bind(this);
			const setMinWidth = this.setPopperMinWidth.bind(this);

			this.$nextTick()
				.then(execInstance)
				.then(setMinWidth);
		},
		updateInstance() {
			this.popperInstance.update();
			this.$nextTick(this.setPopperMinWidth);
		},
		execCreateInstance() {
			if (!this.popperVisible) {
				return;
			}

			if (this.popperInstance) {
				this.popperInstance.destroy();
			}

			const opts = {
				placement: this.placement,
				strategy: this.strategy,
				modifiers: []
			};

			if (typeof this.trackedRefName === 'string') {
				this.trackedRef = this.$parent.$refs[this.trackedRefName];
			} else {
				this.trackedRef = this.trackedRefName;
			}

			if (typeof this.popoverRefName === 'string') {
				this.popperRef = this.$parent.$refs[this.popoverRefName];
			} else {
				this.popperRef = this.popoverRefName;
			}

			if (typeof this.arrowRefName === 'string') {
				this.arrowRef = this.$parent.$refs[this.arrowRefName];
			} else {
				this.arrowRef = this.arrowRefName;
			}

			if (this.arrowRef) {
				opts.modifiers.push({
					name: 'arrow',
					options: {
						element: this.arrowRef
					}
				});
			}

			if (this.modifiers) {
				opts.modifiers.push(...this.modifiers);
			}

			if (this.trackedRef && this.popperRef) {
				this.popperInstance = createPopper(
					this.trackedRef.$el || this.trackedRef,
					this.popperRef.$el || this.popperRef,
					opts
				);
			} else {
				//parent component refs are not rendered
				//console.log('parent component refs are not rendered');
			}
		},
		setPopperMinWidth() {
			if (!this.popperVisible || !this.trackedRef || !this.popperRef) {
				return;
			}

			const reference = this.trackedRef.$el || this.trackedRef;
			const referenceBoundingRect = reference.getBoundingClientRect();

			if (!referenceBoundingRect) return;

			const minWidth = `${referenceBoundingRect.width}px`;

			if (minWidth !== this.popperMinWidth) {
				this.popperMinWidth = minWidth;
				this.$emit('popperMinWidthChanged', minWidth);
			}
		}
	}
};
</script>

<style lang="scss" scoped></style>
